/**
 * useCableJunctionsStandardized Hook
 *
 * Standardized hook for cable junction CRUD operations.
 * Wraps useEntity with junction-specific logic.
 */

import { useMemo, useCallback } from 'react';
import { useEntity } from '@/hooks/queries/useEntityQuery';
import { cableJunctionServiceAdapter } from '@/services/adapters/cableJunctionServiceAdapter';
import { getConnectedPathIds, canDeleteJunction } from '@/services/cableJunctionService';
import type { CableJunction } from '@/types/cablePaths';

export function useCableJunctionsStandardized(floorPlanId: string) {
  const {
    data,
    isLoading,
    error,
    refetch,
    create,
    update,
    delete: deleteJunction,
    ...mutations
  } = useEntity<CableJunction>('cable_junctions', floorPlanId, cableJunctionServiceAdapter);

  /**
   * Create a new junction at the specified position.
   * Name will be auto-generated if not provided.
   */
  const createJunction = useCallback(
    async (junctionData: Partial<CableJunction>, options?: { showSuccessToast?: boolean }) => {
      const junctionWithFloorPlan = {
        ...junctionData,
        floor_plan_id: floorPlanId,
      };
      return create(junctionWithFloorPlan, options);
    },
    [floorPlanId, create],
  );

  /**
   * Update a junction's properties.
   */
  const updateJunction = useCallback(
    async (id: string, updates: Partial<CableJunction>) => {
      return update({ id, updates, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Move a junction to a new position.
   */
  const moveJunction = useCallback(
    async (id: string, x: number, y: number) => {
      return update({ id, updates: { x, y }, showSuccessToast: false });
    },
    [update],
  );

  /**
   * Delete a junction (will fail if it has connected paths).
   */
  const safeDeleteJunction = useCallback(
    async (id: string) => {
      return deleteJunction(id);
    },
    [deleteJunction],
  );

  /**
   * Check if a junction can be safely deleted.
   */
  const canDelete = useCallback(async (junctionId: string) => {
    return canDeleteJunction(junctionId);
  }, []);

  /**
   * Get IDs of paths connected to a junction.
   */
  const getConnectedPaths = useCallback(async (junctionId: string) => {
    return getConnectedPathIds(junctionId);
  }, []);

  /**
   * Get a junction by ID from the cached data.
   */
  const getJunctionById = useCallback(
    (id: string): CableJunction | undefined => {
      return data?.find(junction => junction.id === id);
    },
    [data],
  );

  /**
   * Build a map of junction IDs to their coordinates.
   * Useful for path rendering.
   */
  const junctionCoordMap = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    if (data) {
      for (const junction of data) {
        map.set(junction.id, { x: junction.x, y: junction.y });
      }
    }
    return map;
  }, [data]);

  /**
   * Statistics about junctions.
   */
  const statistics = useMemo(() => {
    if (!data) return { total: 0, withProduct: 0, withNotes: 0 };

    return {
      total: data.length,
      withProduct: data.filter(j => j.product_id).length,
      withNotes: data.filter(j => j.notes).length,
    };
  }, [data]);

  return {
    // Data
    junctions: data ?? [],
    junctionCoordMap,

    // Loading states
    isLoading,
    error,

    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,

    // Operations
    createJunction,
    updateJunction,
    moveJunction,
    deleteJunction: safeDeleteJunction,
    refetch,

    // Utilities
    getJunctionById,
    canDelete,
    getConnectedPaths,

    // Statistics
    statistics,
  };
}
